import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionDivider } from '../components/Layout';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// 農作物データ
const crops = [
  {
    id: 'cucumber',
    name: 'きゅうり',
    dividerTitle: 'cucumber',
    title: '栽培技術',
    subtitle: 'オランダ式高軒高ハウス',
    content: (
      <div className="space-y-6">
        <div>
          <h3 className="heading-3 mb-3" style={{color: '#8b4513'}}>オランダ式高軒高ハウス<span style={{color: '#ff8c00'}}>とは</span></h3>
          <p className="body-text leading-relaxed mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            オランダ式高軒高ハウスは、施設園芸先進国オランダで開発された次世代型温室システムです。
            天井高が5メートル以上と高く設計されており、空間を最大限に活用することで、作物の生育に最適な環境を実現します。
          </p>

          {/* ハウス外観画像 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/greenhouse_exterior.jpg"
                alt="オランダ式高軒高ハウス外観"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>主な<span style={{color: '#ff8c00'}}>特徴</span></h4>
          <ul className="space-y-2 body-text mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            <li>• <strong>高い天井構造</strong>：空間が広く、温度・湿度の管理がしやすい</li>
            <li>• <strong>最新の環境制御システム</strong>：温度、湿度、CO2濃度を自動管理</li>
            <li>• <strong>高い生産性</strong>：年間150トンの生産を実現</li>
            <li>• <strong>年間を通じて収穫</strong>：1年中、高品質なきゅうりをお届け</li>
          </ul>

          {/* ハウス内観画像 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/greenhouse_interior.jpg"
                alt="オランダ式高軒高ハウス内観"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>立石農園での<span style={{color: '#ff8c00'}}>導入効果</span></h4>
          <p className="body-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
            43a（4,300㎡）の栽培面積で年間150トンという高い生産量を達成。
            科学的データ管理により、1年中高品質なきゅうりを生産し、高い評価をいただいています。
          </p>
        </div>

        <div className="border-t pt-6" style={{borderColor: '#e5d3b3'}}>
          <h3 className="heading-3 mb-4" style={{color: '#8b4513'}}>生産データ</h3>

          {/* 年間生産量推移グラフ */}
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm" style={{border: '1px solid #e5d3b3'}}>
            <h4 className="heading-3 mb-4 text-center" style={{color: '#8b4513'}}><span style={{color: '#ff8c00'}}>年間</span>生産量推移（サンプルデータ）</h4>
            <Bar
              data={{
                labels: ['2020年', '2021年', '2022年', '2023年', '2024年', '2025年'],
                datasets: [
                  {
                    label: 'きゅうり生産量（トン）',
                    data: [120, 130, 140, 145, 148, 150],
                    borderColor: '#22c55e',
                    backgroundColor: '#22c55e',
                    tension: 0.4
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: '生産量（トン）'
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    ),
    subtitle2: '養液栽培の詳細',
    content2: (
      <div className="space-y-6">
        <div>
          <h3 className="heading-3 mb-3" style={{color: '#8b4513'}}>養液栽培・隔離培地栽培<span style={{color: '#ff8c00'}}>とは</span></h3>
          <p className="body-text leading-relaxed mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            養液栽培は、土を使わずに養分を溶かした水溶液で作物を育てる栽培方法です。
            隔離培地栽培は、ココヤシ繊維などの培地を使用し、土壌から完全に隔離された環境で栽培を行います。
          </p>

          {/* 養液栽培の写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-3/4 md:w-[30%]">
              <img
                src="/images/products/cucumber_hydroponic_system.jpg"
                alt="養液栽培システム"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}><span style={{color: '#ff8c00'}}>養液栽培</span>の<span style={{color: '#ff8c00'}}>メリット</span></h4>
          <ul className="space-y-2 body-text mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            <li>• <strong>病気のリスク軽減</strong>：土壌病害を回避し、清潔な環境で栽培</li>
            <li>• <strong>養分管理の精密化</strong>：作物に必要な養分を最適な量で供給</li>
            <li>• <strong>安定した品質</strong>：環境を均一に保ち、品質のばらつきを最小化</li>
            <li>• <strong>高い生産効率</strong>：生育スピードが速く、収穫量が増加</li>
            <li>• <strong>環境負荷の低減</strong>：水や肥料の使用量を最適化し、環境に配慮</li>
          </ul>

          {/* 環境制御システムの写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/cucumber_control_system.jpg"
                alt="環境制御システム"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>立石農園の<span style={{color: '#ff8c00'}}>養液栽培システム</span></h4>
          <p className="body-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
            最新のセンサー技術とデータ管理システムを導入し、養分濃度・pH値・水温などを常時モニタリング。
            きゅうりの生育ステージに合わせて、最適な養分バランスを自動調整することで、
            1年中高品質で安全なきゅうりを生産しています。
          </p>
        </div>

        <div className="border-t pt-6" style={{borderColor: '#e5d3b3'}}>
          {/* 月別収穫量グラフ */}
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm" style={{border: '1px solid #e5d3b3'}}>
            <h4 className="heading-3 mb-4 text-center" style={{color: '#8b4513'}}><span style={{color: '#ff8c00'}}>月別</span>収穫量</h4>
            <Bar
              data={{
                labels: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
                datasets: [
                  {
                    label: '月別収穫量（トン）',
                    data: [11.27, 3.51, 12.70, 20.05, 17.20, 23.25, 16.91, 16.84, 9.33, 3.90, 7.24, 6.73],
                    backgroundColor: '#22c55e',
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: '収穫量（トン）'
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'sweetpotato',
    name: 'さつまいも',
    dividerTitle: 'sweetpotato',
    title: '栽培技術',
    subtitle: '露地栽培',
    content: (
      <div className="space-y-6">
        <div>
          <h3 className="heading-3 mb-3" style={{color: '#8b4513'}}>露地栽培<span style={{color: '#ff8c00'}}>とは</span></h3>
          <p className="body-text leading-relaxed mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            露地栽培は、ハウスなどの施設を使わず、自然の太陽光と土壌で作物を育てる伝統的な栽培方法です。
            さつまいもは本来、温暖な気候と水はけの良い土壌を好む作物で、露地栽培に適しています。
          </p>

          {/* さつまいも畑の写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/sweetpotato_field.jpg"
                alt="さつまいも露地栽培"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>主な<span style={{color: '#ff8c00'}}>特徴</span></h4>
          <ul className="space-y-2 body-text mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            <li>• <strong>自然環境を活用</strong>：太陽光と自然の雨を活かした露地栽培</li>
            <li>• <strong>伊万里の山の土を再現</strong>：自作たい肥で理想的な土作り</li>
            <li>• <strong>紅はるかの栽培</strong>：甘くてしっとりとした食感が特徴</li>
            <li>• <strong>年間10トン生産</strong>：栽培面積50a、10月上旬〜11月中旬に収穫</li>
          </ul>

          {/* さつまいもの特徴を示す写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/sweetpotato_features.jpg"
                alt="さつまいもの特徴"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>立石農園での<span style={{color: '#ff8c00'}}>露地栽培</span></h4>
          <p className="body-text leading-relaxed mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            <span style={{color: '#ff8c00'}}>中山間地域</span>の恵まれた自然環境を活かし、山の土を再現した環境でさつまいもを栽培しています。
            紅はるかは糖度が高く、焼き芋にすると特に甘みが際立つ品種で、直売で高い評価をいただいています。
          </p>

          {/* できあがったさつまいもの写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/sweetpotato_harvest.jpg"
                alt="収穫したさつまいも"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    ),
    subtitle2: '自作たい肥',
    content2: (
      <div className="space-y-6">
        <div>
          <h3 className="heading-3 mb-3" style={{color: '#8b4513'}}>自作たい肥<span style={{color: '#ff8c00'}}>とは</span></h3>
          <p className="body-text leading-relaxed mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            立石農園では、木材チップと鶏糞を組み合わせた自作たい肥を使用しています。
            化学肥料に頼らず、有機質を豊富に含んだたい肥で伊万里の山と同等の土づくりを目指すことで、持続可能な農業を実践しています。
          </p>

          {/* 自作たい肥の写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/sweetpotato_compost.jpg"
                alt="自作たい肥"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>自作たい肥の<span style={{color: '#ff8c00'}}>メリット</span></h4>
          <ul className="space-y-2 body-text mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            <li>• <strong>土壌改良効果</strong>：有機質が土壌の保水性と通気性を改善</li>
            <li>• <strong>微生物の活性化</strong>：豊かな土壌微生物が作物の成長を促進</li>
            <li>• <strong>環境負荷の低減</strong>：地域資源を活用した循環型農業</li>
            <li>• <strong>高品質な作物</strong>：自然な甘みと風味が豊かなさつまいもに</li>
            <li>• <strong>コスト削減</strong>：外部から肥料を購入する必要が減少</li>
          </ul>

          {/* たい肥のメリットを示す写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/sweetpotato_soil.jpg"
                alt="良質な土壌"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>立石農園のたい肥<span style={{color: '#ff8c00'}}>作り</span></h4>
          <p className="body-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
            木材チップと鶏糞を適切な比率で混合し、時間をかけて発酵させることで、
            栄養バランスの取れた良質なたい肥を作っています。
            この自作たい肥を使うことで、化学肥料を使わずに健康的で美味しいさつまいもを育てることができます。
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'collaboration',
    name: 'コラボレーション',
    dividerTitle: 'collaboration',
    title: 'コラボレーション',
    subtitle: '様々な取り組み',
    content: (
      <div className="space-y-6">
        <div>
          <h3 className="heading-3 mb-3" style={{color: '#8b4513'}}><span style={{color: '#ff8c00'}}>地域</span>との連携</h3>
          <p className="body-text leading-relaxed mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            立石農園では、地域の皆様や様々な企業・団体との協力関係を大切にしています。
            コラボレーションを通じて、新しい価値を創造し、地域農業の発展に貢献しています。
          </p>

          {/* 地域との連携の写真 */}
          <div className="flex justify-center mb-4">
            <div className="rounded-xl overflow-hidden shadow-md w-full md:w-1/2">
              <img
                src="/images/products/overview.jpg"
                alt="地域との連携"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>主な<span style={{color: '#ff8c00'}}>取り組み</span></h4>
          <ul className="space-y-2 body-text mb-4" style={{color: '#8b4513', opacity: 0.8}}>
            <li>• <strong>地域企業との連携</strong>：商品開発や販路拡大の協力</li>
            <li>• <strong>教育機関との連携</strong>：農業体験や食育プログラムの実施</li>
            <li>• <strong>行政との協働</strong>：地域農業振興プロジェクトへの参加</li>
            <li>• <strong>飲食店との提携</strong>：新鮮な農作物の直接供給</li>
          </ul>

          {/* 取り組み事例：小嶋や */}
          <div className="mb-4">
            <div className="flex justify-center mb-3">
              <img
                src="/images/products/collaboration_kojimaya.jpg"
                alt="小嶋や 焼き芋どら焼き"
                className="rounded-xl shadow-md"
                style={{maxWidth: '400px', width: '100%', height: 'auto'}}
              />
            </div>
            <p className="text-sm text-center mb-2" style={{color: '#8b4513', opacity: 0.8}}>
              「小嶋や」焼き芋どら焼き
            </p>
            <div className="text-center">
              <a
                href="https://www.instagram.com/reels/DOLBIkkElMm/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 text-sm font-medium rounded-lg transition-all"
                style={{
                  color: '#fff',
                  background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(188, 24, 136, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Instagramで見る
              </a>
            </div>
          </div>
        </div>

        <div>
          <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}><span style={{color: '#ff8c00'}}>今後の</span>展開</h4>
          <p className="body-text leading-relaxed" style={{color: '#8b4513', opacity: 0.8}}>
            これからも地域の皆様と手を取り合い、持続可能な農業の実現と地域社会の発展に貢献していきます。
            新しいコラボレーションのご提案も随時お待ちしております。
          </p>
        </div>
      </div>
    )
  }
];

export default function ProductsPage() {
  const [activeTab, setActiveTab] = useState('cucumber');
  const tabNavRef = useRef(null);
  const navigate = useNavigate();

  const activeCrop = crops.find(crop => crop.id === activeTab);

  // タブを切り替えて上にスクロールする関数
  const handleTabClickAndScroll = (tabId) => {
    setActiveTab(tabId);
    if (tabNavRef.current) {
      tabNavRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen relative">

      {/* ヒーローセクション */}
      <section
        className="h-80 md:h-screen flex items-start justify-center md:items-start md:justify-start relative pt-8 md:pt-16 pb-12 px-4 md:pl-48"
        style={{
          width: '100vw',
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url("/images/products/overview_mobile.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          overflow: 'hidden'
        }}
      >
        {/* PCバージョン背景画像 */}
        <div
          className="hidden md:block absolute inset-0 z-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url("/images/products/overview_desktop.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>

        <div style={{
          position: 'relative',
          zIndex: 2
        }} className="text-center md:text-left">
          <h1 className="font-bold text-white leading-tight" style={{
            fontSize: window.innerWidth >= 768 ? '56px' : '24px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
          }}>
            <span className="text-orange-300">栽培・技術</span><br />
            科学的アプローチと<br />
            最新技術による栽培
          </h1>
        </div>
      </section>

      {/* 主な農作物セクション */}
      <div className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-4xl mx-auto">
          <section className="tateishi-section">
            <div className="-mx-4 md:px-6">
              {/* 主な農作物タイトル */}
              <h3 className="heading-2 mb-6 text-center" style={{color: '#8b4513'}}><span style={{color: '#ff8c00'}}>主な</span>農作物</h3>

              <div className="responsive-grid-2">
                {/* カード1：きゅうり */}
                <div className="rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-stone-200 text-center" style={{backgroundColor: '#ffedcfff'}}>
                  <div className="flex justify-center mb-4">
                    <div className="w-28 h-28 md:w-40 md:h-40 rounded-full overflow-hidden shadow-lg">
                      <img
                        src="/images/about/crops1.jpg"
                        alt="きゅうり"
                        className="w-full h-full object-cover object-center"
                        style={{transform: 'scale(1.4)'}}
                      />
                    </div>
                  </div>
                  <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>
                    きゅうり
                  </h4>
                  <div className="space-y-2 caption-text" style={{color: '#8b4513', opacity: 0.8}}>
                    <p>• 年間150トン生産、栽培面積43a</p>
                    <p>• オランダ式高軒高ハウス + 養液栽培</p>
                    <p>• 科学的データ管理により1年中収穫可能</p>
                  </div>
                </div>

                {/* カード2：さつまいも */}
                <div className="rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-stone-200 text-center" style={{backgroundColor: '#ffedcfff'}}>
                  <div className="flex justify-center mb-4">
                    <div className="w-28 h-28 md:w-40 md:h-40 rounded-full overflow-hidden shadow-lg">
                      <img
                        src="/images/about/crops2.jpg"
                        alt="さつまいも"
                        className="w-full h-full object-cover object-center"
                        style={{transform: 'scale(1.4)'}}
                      />
                    </div>
                  </div>
                  <h4 className="heading-3 mb-3" style={{color: '#8b4513'}}>
                    さつまいも（紅はるか）
                  </h4>
                  <div className="space-y-2 caption-text" style={{color: '#8b4513', opacity: 0.8}}>
                    <p>• 年間10トン生産、栽培面積50a</p>
                    <p>• 露地栽培、自作堆肥で土作り</p>
                    <p>• 循環型農業で持続可能な栽培を実践</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* タブナビゲーション */}
      <div ref={tabNavRef} className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-7xl mx-auto">
          <div className="-mx-4 md:px-8">
            <div className="flex justify-center gap-2 md:gap-4 mb-8">
            {crops.map(crop => (
              <button
                key={crop.id}
                onClick={() => setActiveTab(crop.id)}
                className="px-4 md:px-8 py-2 md:py-3 text-sm md:text-lg font-medium rounded-lg transition-all duration-300"
                style={{
                  color: activeTab === crop.id ? '#fff' : '#8b4513',
                  backgroundColor: activeTab === crop.id ? '#8b4513' : 'transparent',
                  border: `2px solid ${activeTab === crop.id ? '#8b4513' : '#e5d3b3'}`,
                  transform: activeTab === crop.id ? 'translateY(-2px)' : 'none',
                  boxShadow: activeTab === crop.id ? '0 4px 12px rgba(139, 69, 19, 0.2)' : 'none'
                }}
              >
                {crop.name}
              </button>
            ))}
            </div>
          </div>
        </div>
      </div>

      {/* SVG区切り */}
      <SectionDivider title={activeCrop.dividerTitle} />

      {/* 選択された農作物の栽培技術セクション */}
      <div className="tateishi-content ml-0 md:ml-40">
        <div className="max-w-7xl mx-auto">
          <section className="tateishi-section">
            <div className="-mx-4 md:px-8">
              {/* 最初のセクション */}
              <div className="text-center mb-8 md:mb-12">
                <h2 className="heading-1 mb-4" style={{color: '#8b4513'}}>
                  {activeCrop.id === 'cucumber' ? (
                    <>
                      <span style={{color: '#ff8c00'}}>栽培</span>技術
                    </>
                  ) : (
                    activeCrop.title
                  )}
                </h2>
                <p className="heading-3" style={{color: '#8b4513', opacity: 0.8}}>
                  {activeCrop.id === 'sweetpotato' ? (
                    <span style={{color: '#ff8c00'}}>露地栽培</span>
                  ) : activeCrop.id === 'collaboration' ? (
                    <>
                      <span style={{color: '#ff8c00'}}>様々な</span>取り組み
                    </>
                  ) : (
                    activeCrop.subtitle
                  )}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-4 md:p-8 mb-12" style={{border: '1px solid #e5d3b3'}}>
                <div className="text-center">
                  {activeCrop.content}
                </div>
              </div>

              {/* 2つ目のセクション（もしあれば） */}
              {activeCrop.subtitle2 && (
                <>
                  <div className="text-center mb-8 md:mb-12">
                    <p className="heading-3" style={{color: '#8b4513', opacity: 0.8}}>
                      {activeCrop.id === 'cucumber' ? (
                        <>
                          <span style={{color: '#ff8c00'}}>養液栽培</span>の詳細
                        </>
                      ) : activeCrop.id === 'sweetpotato' ? (
                        <span style={{color: '#ff8c00'}}>自作たい肥</span>
                      ) : (
                        activeCrop.subtitle2
                      )}
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm p-4 md:p-8 mb-12" style={{border: '1px solid #e5d3b3'}}>
                    <div className="text-center">
                      {activeCrop.content2}
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>

        {/* 下部のタブナビゲーション（枠の外） */}
        <div className="max-w-7xl mx-auto mt-12 mb-8">
          <div className="-mx-4 md:px-8">
          <div className="flex justify-center gap-2 md:gap-4">
            {crops.map(crop => (
              <button
                key={crop.id}
                onClick={() => handleTabClickAndScroll(crop.id)}
                className="px-4 md:px-8 py-2 md:py-3 text-sm md:text-lg font-medium rounded-lg transition-all duration-300"
                style={{
                  color: activeTab === crop.id ? '#fff' : '#8b4513',
                  backgroundColor: activeTab === crop.id ? '#8b4513' : 'transparent',
                  border: `2px solid ${activeTab === crop.id ? '#8b4513' : '#e5d3b3'}`,
                  transform: activeTab === crop.id ? 'translateY(-2px)' : 'none',
                  boxShadow: activeTab === crop.id ? '0 4px 12px rgba(139, 69, 19, 0.2)' : 'none'
                }}
              >
                {crop.name}
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* 購入・問い合わせボタン */}
        <div className="max-w-7xl mx-auto mb-16">
          <div className="-mx-4 md:px-8">
            <div className="text-center space-y-6">
              {/* タイトル */}
              <h3 className="text-lg md:text-xl font-bold" style={{color: '#8b4513'}}>ご購入はこちらから</h3>

              {/* 個人のお客様 */}
              <div className="flex flex-col md:flex-row items-center justify-center gap-3">
                <span className="font-bold" style={{color: '#8b4513'}}>個人のお客様</span>
                <div className="flex gap-3">
                  <a
                    href="https://www.tabechoku.com/producers/3078345?srsltid=AfmBOorXuP8OB-ehEl8b8-qLtXW0qcL4L0KjoOdCkAqzEfyPN5Yo2oGy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2 text-sm font-bold rounded-lg transition-all bg-green-600 hover:bg-green-700 text-white"
                  >
                    食べチョク
                  </a>
                  <a
                    href="https://jp.mercari.com/user/profile/819884723"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2 text-sm font-bold rounded-lg transition-all bg-red-500 hover:bg-red-600 text-white"
                  >
                    メルカリ
                  </a>
                </div>
              </div>

              {/* 業者のお客様 */}
              <div className="flex flex-col md:flex-row items-center justify-center gap-3">
                <span className="font-bold" style={{color: '#8b4513'}}>業者のお客様</span>
                <button
                  onClick={() => navigate('/contact')}
                  className="px-6 py-2 text-sm font-bold rounded-lg transition-all text-white"
                  style={{backgroundColor: '#8b4513'}}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6d3610'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8b4513'}
                >
                  お問い合わせ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}